import axios from "@/lib/axios";

const arrayFromResponse = (data, keys) => {
  for (const key of keys) {
    if (Array.isArray(data?.[key])) {
      return data[key];
    }
  }
  if (Array.isArray(data?.data)) {
    return data.data;
  }
  if (Array.isArray(data)) {
    return data;
  }
  return [];
};

export const getAllHaafPayments = async (financialYearId) => {
  const { data } = await axios.get("/haaf-payments", {
    params: { financialYearId },
  });
  return data.data;
};

export const getHaafPaymentsExport = async ({
  financialYearId,
  groupBy,
  ...filters
}) => {
  const { data } = await axios.get("/haaf-payments/export", {
    params: { financialYearId, groupBy, ...filters },
  });
  return data.data || data;
};

export const downloadHaafAlumniExport = async ({
  financialYearId,
  type,
  ...filters
}) => {
  const response = await axios.get(`/haaf-payments/export/${type}`, {
    params: { financialYearId, ...filters },
    responseType: "blob",
  });
  const disposition = response.headers["content-disposition"] || "";
  const encodedName = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const quotedName = disposition.match(/filename="([^"]+)"/i)?.[1];
  const plainName = disposition.match(/filename=([^;]+)/i)?.[1];
  const filename = encodedName
    ? decodeURIComponent(encodedName)
    : (quotedName || plainName || `haf-${type}-alumni.xlsx`).trim();

  return { blob: response.data, filename };
};

export const getHaafPayment = async (id) => {
  const { data } = await axios.get(`/haaf-payments/${id}`);
  return data.data;
};

export const createHaafPayment = async (paymentData) => {
  const { data } = await axios.post("/haaf-payments", paymentData);
  return data.data;
};

export const createHaafReceiptRenderSession = async ({ payment, variant }) => {
  const { data } = await axios.post("/haaf-payments/receipt/render", {
    payment,
    variant,
  });
  return data.renderSession;
};

export const getHaafReceiptAssetBlob = async (url) => {
  const { data } = await axios.get("/haaf-payments/receipt/asset", {
    params: { url },
    responseType: "blob",
  });
  return data;
};

export const getHaafMeta = async () => {
  const { data } = await axios.get("/haaf-payments/meta");
  return data;
};

export const getHaafFilterOptions = async () => {
  const { data } = await axios.get("/haaf-payments/filter-options");
  return data.data || data;
};

export const getHaafFinancialYears = async () => {
  const { data } = await axios.get("/haaf-payments/financial-years");
  return arrayFromResponse(data, ["financialYears", "years"]);
};

export const createHaafFinancialYear = async (payload) => {
  const { data } = await axios.post("/haaf-payments/financial-years", payload);
  return data.financialYear || data.data || data;
};

export const searchHaafAlumni = async (search) => {
  const { data } = await axios.get("/haaf-payments/alumni", {
    params: {
      search,
      limit: 10,
    },
  });
  return arrayFromResponse(data, ["alumni", "profiles"]);
};

export const updateHaafPayment = async ({ id, ...paymentData }) => {
  const { data } = await axios.put(`/haaf-payments/${id}`, paymentData);
  return data.data;
};

export const deleteHaafPayment = async (id) => {
  const { data } = await axios.delete(`/haaf-payments/${id}`);
  return data.data;
};

export const verifyHaafPayment = async (id) => {
  const { data } = await axios.put(`/haaf-payments/${id}/verify`);
  return data.data;
};

export const verifyAllHaafPayments = async (payload) => {
  const { data } = await axios.patch("/haaf-payments/verify-all", payload);
  return data;
};

export const unverifyHaafPayment = async (id) => {
  const { data } = await axios.put(`/haaf-payments/${id}/unverify`);
  return data.data;
};

export const getChapterHaafPayments = async (financialYearId) => {
  const { data } = await axios.get("/haaf-payments/chapter", {
    params: { financialYearId },
  });
  return data.data;
};

export const getHaafPaymentsByBatch = async (batch, financialYearId) => {
  const { data } = await axios.get(`/haaf-payments/batch/${batch}`, {
    params: { financialYearId },
  });
  return data.data;
};

export const getHaafPaymentStats = async (financialYearId) => {
  const { data } = await axios.get("/haaf-payments/stats", {
    params: { financialYearId },
  });
  return data.data;
};

export const getHaafStatisticsOverview = async (financialYearId) => {
  const { data } = await axios.get("/haaf-payments/statistics/overview", {
    params: { financialYearId },
  });
  return data;
};

export const getHaafStatisticsTimeseries = async (
  financialYearId,
  interval = "day",
) => {
  const { data } = await axios.get("/haaf-payments/statistics/timeseries", {
    params: { financialYearId, interval },
  });
  return data;
};

export const getHaafStatisticsBreakdown = async (
  financialYearId,
  groupBy,
) => {
  const { data } = await axios.get("/haaf-payments/statistics/breakdown", {
    params: { financialYearId, groupBy },
  });
  return data;
};

export const getHaafStatisticsLeaderboard = async (
  financialYearId,
  groupBy,
  limit = 10,
) => {
  const { data } = await axios.get("/haaf-payments/statistics/leaderboard", {
    params: { financialYearId, groupBy, limit },
  });
  return data;
};

export const getHaafStatisticsPending = async (financialYearId) => {
  const { data } = await axios.get("/haaf-payments/statistics/pending", {
    params: { financialYearId },
  });
  return data;
};

export const getHaafStatisticsRecentActivity = async (
  financialYearId,
  limit = 10,
) => {
  const { data } = await axios.get(
    "/haaf-payments/statistics/recent-activity",
    {
      params: { financialYearId, limit },
    },
  );
  return data;
};

export const getHaafStatisticsAlumniCoverage = async (financialYearId) => {
  const { data } = await axios.get(
    "/haaf-payments/statistics/alumni-coverage",
    {
      params: { financialYearId },
    },
  );
  return data;
};

export const getHaafStatisticsComparison = async (
  financialYearId,
  compareWith = "previousFY",
) => {
  const { data } = await axios.get("/haaf-payments/statistics/comparison", {
    params: { financialYearId, compareWith },
  });
  return data;
};

export const getHaafPaymentBatchStats = async (financialYearId) => {
  const { data } = await axios.get("/haaf-payments/batch-stats", {
    params: { financialYearId },
  });
  return data.data;
};

export const getHaafPaymentChapterStats = async (financialYearId) => {
  const { data } = await axios.get("/haaf-payments/chapter-stats", {
    params: { financialYearId },
  });
  return data.data;
};
